import { useSyncExternalStore } from "react";
import { normalizeDisplayName } from "@/engine/state/displayName";

/**
 * The participant's display name, persisted locally and shared with the room
 * through presence/broadcast payloads. Living in a store (instead of local
 * component state) lets the in-room settings panel rename without a rejoin.
 *
 * Beside the name sits the only thing that distinguishes a typed name from a
 * generated one: whether the participant has ever taken the name over. Guessing
 * that by comparing the name to the current avatar suggestion is unreliable —
 * it makes a cleared field refill itself and makes shuffle stop renaming as
 * soon as the two drift apart.
 */
const STORAGE_KEY = "userName";
const CUSTOM_STORAGE_KEY = "userNameIsCustom";

function loadName(): string {
  try {
    return normalizeDisplayName(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "";
  }
}

function loadCustom(name: string): boolean {
  try {
    const raw = localStorage.getItem(CUSTOM_STORAGE_KEY);
    // No flag yet: a name saved before this existed was typed by hand.
    if (raw === null) return name.length > 0;
    return raw === "true";
  } catch {
    return name.length > 0;
  }
}

let state: string = typeof window === "undefined" ? "" : loadName();
let custom: boolean = typeof window === "undefined" ? false : loadCustom(state);
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string {
  return state;
}

function getCustomSnapshot(): boolean {
  return custom;
}

export function getDisplayName(): string {
  return state;
}

/** True once the participant has edited the name themselves. */
export function isDisplayNameCustom(): boolean {
  return custom;
}

function commit(next: string, nextCustom: boolean) {
  if (next === state && nextCustom === custom) return;
  state = next;
  custom = nextCustom;
  try {
    localStorage.setItem(STORAGE_KEY, state);
    localStorage.setItem(CUSTOM_STORAGE_KEY, String(custom));
  } catch {
    /* storage unavailable: keep in-memory only */
  }
  listeners.forEach((l) => l());
}

/**
 * The participant typed this — including clearing the field, which must stay
 * cleared instead of being refilled with the avatar's nickname.
 */
export function setDisplayName(value: string) {
  commit(normalizeDisplayName(value), true);
}

/**
 * A generated nickname following the avatar. A non-empty typed name is kept,
 * while an explicitly cleared field can opt back into generated names when the
 * participant requests a new avatar.
 */
export function setSuggestedDisplayName(value: string) {
  if (custom && state.length > 0) return;
  commit(normalizeDisplayName(value), false);
}

/** Re-reads the persisted name and flag. Used by tests that clear storage. */
export function hydrateDisplayNameFromStorage() {
  state = loadName();
  custom = loadCustom(state);
  listeners.forEach((l) => l());
}

export function useDisplayName(): string {
  return useSyncExternalStore(subscribe, getSnapshot, () => "");
}

export function useIsDisplayNameCustom(): boolean {
  return useSyncExternalStore(subscribe, getCustomSnapshot, () => false);
}
